/**
 * Landing hero: Android / iOS download buttons.
 *
 * TEMPORARY (until Play Store + App Store listings are live):
 * Buttons show a "Coming Soon." modal instead of opening store URLs.
 * Website goes live first while mobile app registration is in progress.
 *
 * TODO — restore store links when apps are published:
 * 1. Replace <button> + modal with <a href={STORE_LINKS.playStore|appStore}> (see git history).
 * 2. Set VITE_PLAY_STORE_URL and VITE_APP_STORE_URL in .env.production if needed.
 * 3. Remove COMING_SOON_MESSAGE, showComingSoon state, and SubscriptionGateModal usage here.
 */
import { useState } from 'react';
import { SubscriptionGateModal } from './SubscriptionGateModal';

/** TEMPORARY — remove when store links are restored (see file header). */
const COMING_SOON_MESSAGE = 'Coming Soon.';

function AndroidIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.6 9.48l1.84-3.38c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.45A8.43 8.43 0 0012 7.63c-1.48 0-2.87.56-3.87 1.54L6.26 5.71c-.19-.28-.55-.37-.84-.22-.3.16-.42.54-.26.85L7 9.48A9.23 9.23 0 002.55 17.75h18.9c0-3.43-1.72-6.48-4.45-8.27zM7.29 14.2a1 1 0 112 0 1 1 0 01-2 0zm9.42 0a1 1 0 112 0 1 1 0 01-2 0z" />
    </svg>
  );
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}

const storeButtonClass =
  'flex items-center gap-4 rounded-2xl px-6 py-4 text-left shadow-lg transition-opacity hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';

export function StoreDownloadButtons() {
  const [showComingSoon, setShowComingSoon] = useState(false);

  const openComingSoon = () => setShowComingSoon(true);

  return (
    <>
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 max-w-lg sm:max-w-none mx-auto">
        <button
          type="button"
          onClick={openComingSoon}
          aria-label="Download for Android — coming soon"
          className={`${storeButtonClass} bg-slate-900 focus-visible:ring-slate-900`}
        >
          <AndroidIcon className="h-10 w-10 shrink-0 text-white" />
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Download for</p>
            <p className="text-lg font-black text-white leading-tight">Android</p>
          </div>
        </button>
        <button
          type="button"
          onClick={openComingSoon}
          aria-label="Download for iOS — coming soon"
          className={`${storeButtonClass} bg-blue-600 shadow-blue-200/50 focus-visible:ring-blue-600`}
        >
          <AppleIcon className="h-9 w-9 shrink-0 text-white" />
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-blue-100">Download for</p>
            <p className="text-lg font-black text-white leading-tight">iOS</p>
          </div>
        </button>
      </div>

      {showComingSoon ? (
        <SubscriptionGateModal message={COMING_SOON_MESSAGE} onOk={() => setShowComingSoon(false)} />
      ) : null}
    </>
  );
}
